import { z } from "zod";
import { META_BUSINESS_VERTICALS, META_TIMEZONES } from "./meta-enums.js";

/**
 * Implantação: o formulário de "cliente novo do zero" (página → BM → conta → pagamento →
 * verificação → pixel → domínio → campanha). O que a API da Meta permite o sistema cria; o
 * resto vira etapa manual com link e "Já fiz".
 */
export const IMPLANTATION_STEP_KEYS = [
  "pagina",
  "instagram",
  "bm",
  "pagina_no_bm",
  "conta",
  "pagamento",
  "verificacao",
  "pixel",
  "dominio",
  "campanha",
] as const;
export type ImplantationStepKey = (typeof IMPLANTATION_STEP_KEYS)[number];

export const IMPLANTATION_STEP_STATUSES = [
  "pendente",
  "executando",
  "concluido",
  "manual",
  "aguardando",
  "erro",
  "pulado",
] as const;
export type ImplantationStepStatus = (typeof IMPLANTATION_STEP_STATUSES)[number];

export const IMPLANTATION_STEPS: Record<
  ImplantationStepKey,
  { label: string; kind: "auto" | "manual"; help: string }
> = {
  pagina: {
    label: "Página do Facebook",
    kind: "manual",
    help: "A Meta não permite criar página por API. Crie a página e escolha ela aqui.",
  },
  instagram: {
    label: "Instagram ligado à página",
    kind: "manual",
    help: "Ligue o Instagram profissional à página (Configurações da página → Contas vinculadas).",
  },
  bm: {
    label: "Business Manager",
    kind: "auto",
    help: "Criado pela API quando a Meta permite; se recusar, vira passo a passo guiado.",
  },
  pagina_no_bm: {
    label: "Página dentro do BM",
    kind: "auto",
    help: "Coloca a página no BM para usar nos anúncios.",
  },
  conta: {
    label: "Conta de anúncio",
    kind: "auto",
    help: "Criada dentro do BM. Moeda e fuso não mudam depois.",
  },
  pagamento: {
    label: "Forma de pagamento",
    kind: "manual",
    help: "Só pode ser cadastrada na Meta (cartão, Pix ou boleto). Sem ela os anúncios não rodam.",
  },
  verificacao: {
    label: "Verificação da empresa",
    kind: "manual",
    help: "Envio do CNPJ e comprovante no BM. A Meta leva de dias a semanas para aprovar.",
  },
  pixel: {
    label: "Pixel (conjunto de dados)",
    kind: "auto",
    help: "Criado no BM e liberado para a conta de anúncio.",
  },
  dominio: {
    label: "Domínio do site no BM",
    kind: "manual",
    help: "Adicione e verifique o domínio em Segurança da marca → Domínios.",
  },
  campanha: {
    label: "Primeira campanha (rascunho)",
    kind: "auto",
    help: "Cria um rascunho no assistente para revisar e publicar pausado.",
  },
};

const SAO_PAULO_TZ = META_TIMEZONES.find((t) => t.name === "America/Sao_Paulo")?.id ?? 25;

export const IMPLANTATION_OBJECTIVES = [
  "OUTCOME_LEADS",
  "OUTCOME_TRAFFIC",
  "OUTCOME_SALES",
  "OUTCOME_ENGAGEMENT",
] as const;

const metaId = z
  .string()
  .trim()
  .regex(/^\d{1,30}$/, "Escolha um item da lista.");
const opt = <T extends z.ZodType>(s: T) => s.nullable().default(null);

export const createImplantationSchema = z
  .object({
    name: z.string().trim().min(2, "Dê um nome para a implantação.").max(100),
    connectionId: z.uuid("Escolha a conta Meta conectada."),
    pageMetaId: opt(metaId),
    business: z.discriminatedUnion("mode", [
      z.object({ mode: z.literal("existente"), businessId: z.uuid("Escolha o BM.") }),
      z.object({
        mode: z.literal("novo"),
        name: z.string().trim().min(2, "Informe o nome do BM.").max(100),
        vertical: z.enum(META_BUSINESS_VERTICALS, { message: "Escolha o segmento." }),
        timezoneId: z.number().int().default(SAO_PAULO_TZ),
      }),
    ]),
    adAccount: z.discriminatedUnion("mode", [
      z.object({
        mode: z.literal("existente"),
        adAccountMetaId: z.string().regex(/^act_\d{1,30}$/, "Escolha a conta."),
      }),
      z.object({
        mode: z.literal("nova"),
        name: z.string().trim().min(2, "Informe o nome da conta.").max(100),
      }),
    ]),
    pixel: z.object({
      create: z.boolean().default(true),
      name: z.string().trim().max(100).default(""),
    }),
    domain: z
      .string()
      .trim()
      .max(253)
      .regex(/^$|^([a-z0-9-]+\.)+[a-z]{2,}$/i, "Domínio inválido (ex.: empresa.com.br).")
      .default(""),
    campaign: z.object({
      create: z.boolean().default(true),
      name: z.string().trim().max(200).default(""),
      objective: z.enum(IMPLANTATION_OBJECTIVES).default("OUTCOME_LEADS"),
      dailyBudgetCents: z.number().int().min(0).max(100_000_000).default(4000),
      link: z.string().trim().max(2000).default(""),
      primaryText: z.string().trim().max(5000).default(""),
      headline: z.string().trim().max(255).default(""),
    }),
  })
  .superRefine((v, ctx) => {
    if (v.pixel.create && v.pixel.name.length < 2)
      ctx.addIssue({
        code: "custom",
        path: ["pixel", "name"],
        message: "Informe o nome do pixel.",
      });
    if (v.campaign.create && v.campaign.name.length < 2)
      ctx.addIssue({
        code: "custom",
        path: ["campaign", "name"],
        message: "Informe o nome da campanha.",
      });
  });
export type CreateImplantationInput = z.infer<typeof createImplantationSchema>;

export const updateImplantationStepSchema = z.object({
  status: z.enum(IMPLANTATION_STEP_STATUSES),
  message: z.string().max(2000).nullable().default(null),
  /** Ids criados na etapa (ex.: { businessMetaId, adAccountMetaId, pixelId, draftId }). */
  result: z.record(z.string(), z.string()).nullable().default(null),
});
export type UpdateImplantationStepInput = z.infer<typeof updateImplantationStepSchema>;

/** Só a página pode ser escolhida depois (quando foi criada fora do sistema). */
export const updateImplantationSchema = z
  .object({
    pageMetaId: metaId.optional(),
    /** Novo nome do BM (quando a Meta recusou o nome, ex.: duplicado). */
    businessName: z.string().trim().min(2, "Informe o nome do BM.").max(100).optional(),
  })
  .refine((v) => v.pageMetaId !== undefined || v.businessName !== undefined, {
    message: "Nada para atualizar.",
  });

export interface ImplantationStepDto {
  key: ImplantationStepKey;
  label: string;
  kind: "auto" | "manual";
  help: string;
  status: ImplantationStepStatus;
  message: string | null;
  result: Record<string, string> | null;
  updatedAt: string | null;
}

export type ImplantationStatus = "em_andamento" | "aguardando_manual" | "concluida" | "com_erro";

export interface ImplantationDto {
  id: string;
  name: string;
  status: ImplantationStatus;
  input: CreateImplantationInput;
  steps: ImplantationStepDto[];
  createdAt: string;
  updatedAt: string;
}

/** Status geral a partir das etapas. */
export function implantationStatus(
  steps: { status: ImplantationStepStatus }[],
): ImplantationStatus {
  if (steps.some((s) => s.status === "erro")) return "com_erro";
  if (steps.every((s) => s.status === "concluido" || s.status === "pulado")) return "concluida";
  if (steps.some((s) => s.status === "pendente" || s.status === "executando"))
    return "em_andamento";
  return "aguardando_manual";
}

/** Etapas iniciais: pulos conforme as escolhas do formulário. */
export function initialImplantationSteps(
  input: CreateImplantationInput,
): { key: ImplantationStepKey; status: ImplantationStepStatus }[] {
  return IMPLANTATION_STEP_KEYS.map((key) => {
    const meta = IMPLANTATION_STEPS[key];
    let status: ImplantationStepStatus = meta.kind === "manual" ? "manual" : "pendente";
    if (key === "pagina" && input.pageMetaId) status = "concluido";
    if (key === "bm" && input.business.mode === "existente") status = "concluido";
    if (key === "conta" && input.adAccount.mode === "existente") status = "concluido";
    if (key === "pixel" && !input.pixel.create) status = "pulado";
    if (key === "campanha" && !input.campaign.create) status = "pulado";
    if (key === "dominio" && !input.domain) status = "pulado";
    return { key, status };
  });
}

export const createBusinessPixelSchema = z.object({
  name: z.string().trim().min(2, "Informe o nome do pixel.").max(100),
  /** Conta de anúncio do mesmo BM que passa a usar o pixel. */
  adAccountMetaId: z
    .string()
    .regex(/^act_\d{1,30}$/)
    .nullable()
    .default(null),
});
export type CreateBusinessPixelInput = z.infer<typeof createBusinessPixelSchema>;

export const claimBusinessPageSchema = z.object({ pageMetaId: metaId });
